import { ImageViewer } from '@dealstack/frontend/shared/components/DocumentViewerV2/ImageViewer/ImageViewer';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import { isImageExtension } from '@dealstack/shared/lib/common/file/file.ts';
import { forwardRef, lazy, Suspense } from 'react';
import type { DocumentViewerV2Props } from './DocumentViewerV2.types';

const PdfViewer = lazy(() =>
	import('@dealstack/frontend/shared/components/DocumentViewerV2/PdfViewer/PdfViewer').then(
		(m) => ({ default: m.PdfViewer })
	)
);
export const DocumentViewerV2 = forwardRef<HTMLDivElement, DocumentViewerV2Props>(
	(
		{
			file,
			fileName,
			fileExtension,
			details,
			showDetailsByDefault,
			isLoading,
			onBackArrowClick,
			renderForms,
			onDocumentClick,
			onCancelPassword,
			showBackButton = true,
			className,
			pageProps,
			onDownload,
		}: DocumentViewerV2Props,
		ref
	) => {
		if (isLoading) {
			return <InlineLoader />;
		}

		if (isImageExtension(fileExtension as FileExtension)) {
			return (
				<ImageViewer
					className={className}
					fileName={fileName}
					file={file}
					fileExtension={fileExtension as FileExtension}
					details={details}
					showDetailsByDefault={showDetailsByDefault}
					isLoading={isLoading}
					onBackArrowClick={onBackArrowClick}
					ref={ref}
				/>
			);
		}

		if (
			['xls', 'xlsx', 'csv', 'doc', 'docx', 'ppt', 'pptx'].includes(
				fileExtension as FileExtension
			) &&
			typeof file === 'string'
		) {
			const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file)}`;
			return (
				<iframe
					src={officeViewerUrl}
					style={{ width: '100%', height: '100vh', border: 'none' }}
					title="Excel Viewer"
				/>
			);
		}

		return (
			<Suspense fallback={<InlineLoader />}>
				<PdfViewer
					className={className}
					fileName={fileName}
					file={file}
					details={details}
					showDetailsByDefault={showDetailsByDefault}
					isLoading={isLoading}
					onBackArrowClick={onBackArrowClick}
					renderForms={renderForms}
					onDocumentClick={onDocumentClick}
					onCancelPassword={onCancelPassword}
					onDownload={onDownload}
					pageProps={pageProps}
					ref={ref}
					showBackButton={showBackButton}
				/>
			</Suspense>
		);
	}
);
